/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, Grid, TextField, Typography2, VisuallyHidden } from '@coursera/cds-core';

import _t from 'i18n!nls/learning-assistant-chat-ai';

type Props = {
  title: string;
  placeholder?: string;
  onSubmit(
    e: (React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) & {
      data: { userComments: string | undefined };
    }
  ): void;
  onClose(e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>): void;
  onCancel(e: React.MouseEvent<Element, MouseEvent>): void;
};

const FeedbackModal = ({ title, placeholder, onSubmit, onClose, onCancel, ...rest }: Props) => {
  const { Content, Actions } = Dialog;
  const [show, setShow] = React.useState(true);
  const textFieldRef = React.useRef<HTMLInputElement>(null);

  const handleOnClose = (e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) => {
    setShow(false);
    onClose(e);
  };

  const handleOnSubmit = (
    e: (React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>) & {
      data: { userComments: string | undefined };
    }
  ) => {
    setShow(false);
    onSubmit(e);
  };

  const handleOnCancel = (e: React.MouseEvent<Element, MouseEvent>) => {
    setShow(false);
    onCancel(e);
  };

  return (
    <Dialog aria-labelledby="dialog-header" open={show} sticky="actions" onClose={handleOnClose} {...rest}>
      <VisuallyHidden id="dialog-header">{_t('Response feedback')}</VisuallyHidden>
      <Content id="dialog-content">
        <Grid container>
          <Grid item sm={12} xs={12}>
            <TextField
              optional
              maxLength={255}
              placeholder={placeholder}
              inputRef={textFieldRef}
              multiline
              fullWidth
              label={title}
              renderLabel={() => (
                <Typography2 variant="titleMedium" component="label">
                  {title}
                </Typography2>
              )}
            />
          </Grid>
        </Grid>
      </Content>

      <Actions
        css={css`
          display: flex;
          gap: var(--cds-spacing-200);
        `}
      >
        <Button
          size="medium"
          variant="primary"
          onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
            handleOnSubmit({ ...e, data: { userComments: textFieldRef.current?.value } })
          }
        >
          {_t('Submit')}
        </Button>

        <Button size="medium" variant="secondary" onClick={handleOnCancel}>
          {_t('Cancel')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default FeedbackModal;
